import { Link } from "react-router-dom";
import { MarcaFotoInterativa } from "./MarcaFotoInterativa.js";

type TipoLegal = "privacidade" | "termos" | "imagens-ia";

const CONTEUDOS: Record<
  TipoLegal,
  { titulo: string; resumo: string; secoes: Array<[string, string[]]> }
> = {
  privacidade: {
    titulo: "Aviso de privacidade",
    resumo: "Saiba quais dados usamos, por que usamos e como fazer uma solicitação.",
    secoes: [
      [
        "Dados de interesse comercial",
        [
          "Quando você solicita uma demonstração, recebemos nome, organização, e-mail, WhatsApp e informações sobre o evento. Usamos esses dados para responder ao pedido e preparar uma conversa ou proposta.",
          "O consentimento para receber novidades é separado da autorização necessária para responder à solicitação. Você pode pedir a interrupção das comunicações de marketing a qualquer momento.",
        ],
      ],
      [
        "Medição da landing page",
        [
          "Com sua permissão, registramos interações como cliques, seções abertas e envio do formulário, junto a um identificador aleatório da sessão. Esses registros não incluem nome, e-mail ou telefone. Não usamos rastreadores publicitários de terceiros nesta página.",
          "A preferência fica armazenada no seu navegador e pode ser apagada pelas configurações do próprio navegador.",
        ],
      ],
      [
        "Dados de participantes",
        [
          "Durante uma experiência, o organizador define a finalidade do atendimento e as autorizações aplicáveis. Nome, telefone, fotografia e criações associadas podem ser tratados para gerar, localizar, exibir e entregar a lembrança conforme a dinâmica informada.",
        ],
      ],
      [
        "Envio, exibição e publicidade",
        [
          "Receber a foto, aparecer no Kiosk e receber publicidade futura são usos diferentes. Eles não devem ser tratados como uma única autorização.",
          "O participante pode compartilhar voluntariamente as imagens que recebeu. Isso não autoriza automaticamente o organizador ou o Foto Interativa a usar a fotografia em publicidade, campanhas ou redes institucionais.",
        ],
      ],
      [
        "Fornecedores e conservação",
        [
          "Os dados podem passar pelos fornecedores necessários para geração das imagens, hospedagem, armazenamento e envio. Os prazos para leads, fotos, atendimentos, medição e backups precisam ser definidos antes da publicação.",
        ],
      ],
      [
        "Seus direitos",
        [
          "Você pode pedir acesso, correção, informação, retirada de consentimento, remoção da galeria ou exclusão. O canal específico e o prazo de atendimento precisam ser definidos antes da publicação.",
        ],
      ],
    ],
  },
  termos: {
    titulo: "Termos de uso",
    resumo: "Condições essenciais para utilizar o Foto Interativa em uma ação ou evento.",
    secoes: [
      [
        "Finalidade do serviço",
        [
          "O Foto Interativa apoia a criação, organização, exibição e entrega de composições artísticas personalizadas. O serviço não representa uma certificação de fatos, pessoas, manifestações ou acontecimentos retratados de forma criativa.",
        ],
      ],
      [
        "Responsabilidade do organizador",
        [
          "Quem organiza a ação deve informar os participantes sobre a dinâmica e obter os consentimentos ou bases legais aplicáveis. Também deve garantir autorização para utilizar marcas, capas, fotografias, vídeos, identidades, personagens e demais materiais enviados ao sistema.",
        ],
      ],
      [
        "Conteúdo e aprovação",
        [
          "A equipe responsável pelo evento deve revisar cada criação antes da exibição ou do envio. Resultados inadequados, ofensivos, enganosos ou que violem direitos não devem ser aprovados.",
        ],
      ],
      [
        "Disponibilidade",
        [
          "A operação pode depender de conexão com a internet e de serviços externos de geração, armazenamento e mensageria. Escopo, volume, suporte e condições comerciais são definidos na contratação de cada ação.",
        ],
      ],
      [
        "Uso aceitável",
        [
          "Não é permitido usar o serviço para produzir conteúdo ilegal, discriminatório, abusivo, fraudulento ou sem as autorizações necessárias. O acesso pode ser suspenso para proteger participantes, terceiros ou a integridade da plataforma.",
        ],
      ],
    ],
  },
  "imagens-ia": {
    titulo: "Política de imagens e inteligência artificial",
    resumo: "Transparência sobre o caráter artístico das criações e os cuidados antes da entrega.",
    secoes: [
      [
        "Natureza das imagens",
        [
          "As criações do Foto Interativa são composições artísticas geradas ou transformadas com auxílio de inteligência artificial. Elas não documentam uma manifestação espiritual, um encontro histórico ou a presença real de uma pessoa representada na cena.",
        ],
      ],
      [
        "Referências e identidade",
        [
          "O resultado pode combinar a fotografia do participante com cenários, figuras, capas, marcas, mensagens ou a identidade visual do evento. Esses materiais precisam ter origem e uso autorizados pelo organizador.",
        ],
      ],
      [
        "Revisão humana",
        [
          "Toda composição deve ser conferida antes de ser aprovada, exibida ou enviada. A equipe pode solicitar nova geração quando encontrar deformações, troca de identidade, conteúdo impróprio ou qualquer resultado que não corresponda à proposta.",
        ],
      ],
      [
        "Duas entregas possíveis",
        [
          "O participante pode receber a composição artística criada com IA e também a peça final personalizada. Na peça final, a faixa superior pode conter a capa de um livro, a identidade do evento, uma campanha ou outra arte autorizada.",
        ],
      ],
      [
        "Respeito e contexto",
        [
          "A tecnologia deve servir à memória do encontro sem explorar crenças, vulnerabilidades ou a imagem do participante. A comunicação da experiência precisa ser clara, respeitosa e coerente com o público presente.",
        ],
      ],
      [
        "Compartilhamento e publicidade",
        [
          "O participante pode compartilhar voluntariamente as imagens que recebeu. Isso não autoriza automaticamente o organizador ou o Foto Interativa a usar a fotografia em publicidade, campanhas ou redes institucionais. Esses usos exigem autorização própria.",
        ],
      ],
    ],
  },
};

export function LegalPage({ tipo }: { tipo: TipoLegal }) {
  const conteudo = CONTEUDOS[tipo];
  return (
    <div className="landing-clara min-h-screen bg-lp-marfin text-lp-tinta">
      <header className="border-b border-lp-linha bg-lp-papel">
        <div className="mx-auto flex min-h-20 max-w-4xl items-center justify-between px-5 sm:px-8">
          <MarcaFotoInterativa />
          <Link to="/" className="text-sm font-semibold text-lp-verde hover:text-lp-verde-escuro">
            Voltar à página inicial
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-14 sm:px-8 sm:py-20">
        <p className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-lp-ouro-escuro">
          Informações do serviço
        </p>
        <h1 className="mt-4 font-display text-[2.75rem] leading-tight tracking-[-0.035em] text-lp-verde sm:text-[4rem]">
          {conteudo.titulo}
        </h1>
        <p className="mt-5 max-w-[60ch] text-base leading-relaxed text-lp-suave">
          {conteudo.resumo}
        </p>
        <p className="mt-5 rounded-xl border border-lp-ouro/30 bg-white px-4 py-3 text-[0.75rem] leading-relaxed text-lp-suave">
          <strong className="text-lp-tinta">Versão informativa para revisão:</strong> este texto
          descreve o funcionamento atual do produto e deve passar por revisão jurídica antes da
          publicação em produção.
        </p>
        <div className="mt-12 space-y-10">
          {conteudo.secoes.map(([titulo, paragrafos]) => (
            <section key={titulo}>
              <h2 className="font-display text-[1.65rem] text-lp-verde">{titulo}</h2>
              <div className="mt-4 space-y-4">
                {paragrafos.map((paragrafo) => (
                  <p
                    key={paragrafo}
                    className="max-w-[72ch] text-[0.9375rem] leading-7 text-lp-suave"
                  >
                    {paragrafo}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
        <p className="mt-14 border-t border-lp-linha pt-6 text-[0.75rem] text-lp-suave">
          Atualizado em 20 de agosto de 2026.
        </p>
      </main>
    </div>
  );
}
